'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState, useCallback, useMemo, Suspense } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useInstantSearch } from 'react-instantsearch';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import ProductListingPageName from 'components/consumer/ProductListingPage/components/ProductListingPageName';
import DesktopShowHideFiltersButton from 'components/consumer/ProductListingPage/components/desktop/DesktopShowHideFiltersButton';
import MobileGridColumnToggle from 'components/consumer/ProductListingPage/components/mobile/MobileGridColumnToggle';
import DesktopProductListingSidebarContainer from 'components/consumer/ProductListingPage/components/desktop/DesktopProductListingSidebarContainer';
import useAdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/useAdvancedSearchContext';
import AdvancedSearchProductHits from 'components/consumer/consumer-search/advanced-search-next/components/AdvancedSearchProductHits';
import DesktopProductListingColorFilter from 'components/consumer/consumer-search/advanced-search-next/components/AlgoliaHits/components/DesktopProductListingColorFilter';
import ProductListingSortBy from 'components/consumer/consumer-search/advanced-search-next/components/AlgoliaHits/components/ProductListingSortBy';
import ProductListingPageWidgets from 'components/consumer/ProductListingPageWidgets';
import ProductListingPageDescriptionShell from 'components/consumer/consumer-search/advanced-search-next/components/ProductListingPageDescriptionUI';
import useWarehouseIdForUser from 'global-state/warehouseIdForUser/useWarehouseIdForUser';
import CategoryAlgoliaMobileFilters from './CategoryAlgoliaMobileFilters';
import useCategoryAlgoliaAnalytics from '../hooks/useCategoryAlgoliaAnalytics';
import CategoryAlgoliaSidebar from './CategoryAlgoliaSidebar';
import CategoryCollectionBackLink from './CategoryCollectionBackLink';
import CategoryAlgoliaJSONLD from './CategoryAlgoliaJSONLD';
import CategorySeoFallback from './CategorySeoFallback';
import CategoryAlgoliaResultsCount from './CategoryAlgoliaResultsCount';
import { isCollectionPageSlug } from '../categoryPageSlugUtils';
import { PAGE_TYPE, getCategoryPageTypeConfig } from '../categoryPageTypes';

// Hoisted so an identity with no report yet reads as a stable reference — the
// list feeds effect dependency lists in the analytics hook.
const EMPTY_PRODUCTS = [];
const CategoryAlgoliaLayout = t0 => {
  const $ = _c(101);
  const {
    slug,
    pageType: t1,
    serverCategoryData,
    serverSeoResolved: t2,
    initialProducts,
    selectedFacets,
    onToggleFacet,
    onClearFacets
  } = t0;
  const pageType = t1 === undefined ? PAGE_TYPE.CATEGORY : t1;
  const serverSeoResolved = t2 === undefined ? true : t2;
  let t3;
  if ($[0] !== pageType) {
    t3 = getCategoryPageTypeConfig(pageType);
    $[0] = pageType;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const pageConfig = t3;
  const {
    currentProductIndex
  } = useAdvancedSearchContext();
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      skipSuspense: true
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    results,
    status
  } = useInstantSearch(t4);
  const warehouseId = useWarehouseIdForUser();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {};
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const [enrichedProductsByIndex, setEnrichedProductsByIndex] = useState(t5);
  let t6;
  if ($[4] !== pageConfig.expandsVariants || $[5] !== pageConfig.keepUnmatchedProducts || $[6] !== pageConfig.variantWarehouseField || $[7] !== warehouseId) {
    t6 = pageConfig.expandsVariants && pageConfig.variantWarehouseField ? {
      variantWarehouseField: pageConfig.variantWarehouseField,
      warehouseId,
      keepUnmatchedProducts: pageConfig.keepUnmatchedProducts
    } : null;
    $[4] = pageConfig.expandsVariants;
    $[5] = pageConfig.keepUnmatchedProducts;
    $[6] = pageConfig.variantWarehouseField;
    $[7] = warehouseId;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const variantMatching = t6;
  const enrichedProductsKey = variantMatching ? `${currentProductIndex}|${variantMatching.warehouseId}` : currentProductIndex;
  const enrichedProducts = enrichedProductsByIndex[enrichedProductsKey] ?? EMPTY_PRODUCTS;
  const hasEnrichedProductsReport = enrichedProductsByIndex[enrichedProductsKey] !== undefined;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setIsSidebarOpen(_temp);
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const toggleSidebar = t7;
  let t8;
  if ($[10] !== enrichedProductsKey) {
    t8 = products => {
      setEnrichedProductsByIndex(prev_0 => prev_0[enrichedProductsKey]?.length === 0 && products.length === 0 ? prev_0 : {
        ...prev_0,
        [enrichedProductsKey]: products
      });
    };
    $[10] = enrichedProductsKey;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleProductsChange = t8;
  const categoryData = serverCategoryData?.getCategoryPage;
  const pageName = categoryData?.pageName || slug;
  const t9 = categoryData?.pageId;
  let t10;
  if ($[12] !== enrichedProducts || $[13] !== pageName || $[14] !== pageType || $[15] !== t9) {
    t10 = {
      pageId: t9,
      pageName,
      products: enrichedProducts,
      pageType
    };
    $[12] = enrichedProducts;
    $[13] = pageName;
    $[14] = pageType;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  useCategoryAlgoliaAnalytics(t10);
  const productsCount = results?.nbHits || 0;
  const hasProductsLoaded = status === "idle" || status === "error";
  const isDefaultSortIndex = currentProductIndex === pageConfig.defaultSortIndex;
  const canSeedServerProducts = isDefaultSortIndex && !hasEnrichedProductsReport;
  let t11;
  if ($[17] !== categoryData || $[18] !== serverSeoResolved) {
    t11 = !serverSeoResolved && <CategorySeoFallback categoryData={categoryData} />;
    $[17] = categoryData;
    $[18] = serverSeoResolved;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = categoryData?.pageLongName || pageName;
  const t13 = categoryData?.pageLongDescription || "";
  const t14 = categoryData?.pageId;
  const t15 = results?.nbHits;
  let t16;
  if ($[20] !== currentProductIndex || $[21] !== enrichedProducts || $[22] !== hasProductsLoaded || $[23] !== t12 || $[24] !== t13 || $[25] !== t14 || $[26] !== t15) {
    t16 = <CategoryAlgoliaJSONLD products={enrichedProducts} pageName={t12} pageDescription={t13} pageId={t14} totalHits={t15} currentProductIndex={currentProductIndex} hasProductsLoaded={hasProductsLoaded} />;
    $[20] = currentProductIndex;
    $[21] = enrichedProducts;
    $[22] = hasProductsLoaded;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = classNames("px-3 py-4 space-y-1", "md:px-0 md:py-0 md:space-y-0", "md:items-center md:bg-white md:flex md:h-[53px] md:sticky md:z-[11] md:gap-5 md:justify-between", "md:top-[117px]");
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== pageName) {
    t18 = <ProductListingPageName pageName={pageName} showPageNameWhileLoading={true} />;
    $[29] = pageName;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  let t20;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <CategoryAlgoliaResultsCount />;
    t20 = <DesktopShowHideFiltersButton onClick={toggleSidebar} />;
    $[31] = t19;
    $[32] = t20;
  } else {
    t19 = $[31];
    t20 = $[32];
  }
  let t21;
  if ($[33] !== t18) {
    t21 = <div className="md:flex md:gap-5 md:items-center">{t18}{t19}{t20}</div>;
    $[33] = t18;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  let t23;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = classNames("flex flex-row justify-between gap-3 py-3", "md:block md:gap-0 md:py-0");
    t23 = classNames("flex-1 flex gap-1", "md:flex-initial md:block md:gap-0");
    $[35] = t22;
    $[36] = t23;
  } else {
    t22 = $[35];
    t23 = $[36];
  }
  const t24 = categoryData?.filters;
  let t25;
  if ($[37] !== onToggleFacet || $[38] !== pageType || $[39] !== productsCount || $[40] !== selectedFacets || $[41] !== t24) {
    t25 = <CategoryAlgoliaMobileFilters filters={t24} pageType={pageType} selectedFacets={selectedFacets} onToggleFacet={onToggleFacet} productsCount={productsCount} />;
    $[37] = onToggleFacet;
    $[38] = pageType;
    $[39] = productsCount;
    $[40] = selectedFacets;
    $[41] = t24;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== pageConfig.showColorFilter) {
    t26 = pageConfig.showColorFilter && <DesktopProductListingColorFilter />;
    $[43] = pageConfig.showColorFilter;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== pageConfig.defaultSortIndex || $[46] !== pageConfig.sortOptions) {
    t27 = <ProductListingSortBy sortByOptions={pageConfig.sortOptions} defaultProductIndex={pageConfig.defaultSortIndex} />;
    $[45] = pageConfig.defaultSortIndex;
    $[46] = pageConfig.sortOptions;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== t26 || $[49] !== t27) {
    t28 = <div className="flex-1 md:flex-initial md:flex md:flex-row md:gap-4 md:items-center">{t26}{t27}</div>;
    $[48] = t26;
    $[49] = t27;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  let t29;
  if ($[51] !== t25 || $[52] !== t28) {
    t29 = <div className={t23}>{t25}{t28}</div>;
    $[51] = t25;
    $[52] = t28;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  let t30;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <MobileGridColumnToggle />;
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  let t31;
  if ($[55] !== t29) {
    t31 = <div className={t22}>{t29}{t30}</div>;
    $[55] = t29;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  let t32;
  if ($[57] !== t21 || $[58] !== t31) {
    t32 = <div className={t17}>{t21}{t31}</div>;
    $[57] = t21;
    $[58] = t31;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] !== pageConfig.showCollectionBackLink || $[61] !== slug) {
    t33 = (pageConfig.showCollectionBackLink || isCollectionPageSlug(slug)) && <CategoryCollectionBackLink />;
    $[60] = pageConfig.showCollectionBackLink;
    $[61] = slug;
    $[62] = t33;
  } else {
    t33 = $[62];
  }
  const t34 = categoryData?.filters;
  let t35;
  if ($[63] !== onClearFacets || $[64] !== onToggleFacet || $[65] !== pageName || $[66] !== pageType || $[67] !== selectedFacets || $[68] !== t34) {
    t35 = <CategoryAlgoliaSidebar pageName={pageName} pageType={pageType} filters={t34} selectedFacets={selectedFacets} onToggleFacet={onToggleFacet} onClearFacets={onClearFacets} />;
    $[63] = onClearFacets;
    $[64] = onToggleFacet;
    $[65] = pageName;
    $[66] = pageType;
    $[67] = selectedFacets;
    $[68] = t34;
    $[69] = t35;
  } else {
    t35 = $[69];
  }
  let t36;
  if ($[70] !== isSidebarOpen || $[71] !== t33 || $[72] !== t35) {
    t36 = <div className="hidden md:block"><DesktopProductListingSidebarContainer isOpen={isSidebarOpen}>{t33}{t35}</DesktopProductListingSidebarContainer></div>;
    $[70] = isSidebarOpen;
    $[71] = t33;
    $[72] = t35;
    $[73] = t36;
  } else {
    t36 = $[73];
  }
  const t37 = canSeedServerProducts ? initialProducts : undefined;
  const t38 = canSeedServerProducts ? serverCategoryData?.getCategoryPage : undefined;
  let t39;
  if ($[74] !== currentProductIndex || $[75] !== handleProductsChange || $[76] !== isDefaultSortIndex || $[77] !== t37 || $[78] !== t38 || $[79] !== variantMatching) {
    t39 = <AdvancedSearchProductHits key={currentProductIndex} onProductsChange={handleProductsChange} initialProducts={t37} seedServerData={t38} eagerFirstPageImages={isDefaultSortIndex} enableShowAllButton={true} variantMatching={variantMatching} />;
    $[74] = currentProductIndex;
    $[75] = handleProductsChange;
    $[76] = isDefaultSortIndex;
    $[77] = t37;
    $[78] = t38;
    $[79] = variantMatching;
    $[80] = t39;
  } else {
    t39 = $[80];
  }
  const t40 = categoryData?.pageLongDescription || "";
  let t41;
  if ($[81] !== pageName || $[82] !== t40) {
    t41 = <ProductListingPageDescriptionShell pageLongDescription={t40} pageName={pageName} />;
    $[81] = pageName;
    $[82] = t40;
    $[83] = t41;
  } else {
    t41 = $[83];
  }
  let t42;
  if ($[84] !== t39 || $[85] !== t41) {
    t42 = <div className="md:flex md:flex-col md:gap-8 md:mb-8 md:flex-1">{t39}{t41}</div>;
    $[84] = t39;
    $[85] = t41;
    $[86] = t42;
  } else {
    t42 = $[86];
  }
  let t43;
  if ($[87] !== t36 || $[88] !== t42) {
    t43 = <div className="md:flex md:flex-row md:gap md:mt-2">{t36}{t42}</div>;
    $[87] = t36;
    $[88] = t42;
    $[89] = t43;
  } else {
    t43 = $[89];
  }
  let t44;
  if ($[90] !== categoryData?.pageId || $[91] !== pageConfig.showBestSellersCarousel) {
    t44 = pageConfig.showBestSellersCarousel && <Suspense fallback={null}><ProductListingPageWidgets categoryId={categoryData?.pageId} /></Suspense>;
    $[90] = categoryData?.pageId;
    $[91] = pageConfig.showBestSellersCarousel;
    $[92] = t44;
  } else {
    t44 = $[92];
  }
  let t45;
  if ($[93] !== t32 || $[94] !== t43 || $[95] !== t44) {
    t45 = <FixedWidthContainer wide={true} noMobilePadding={true}>{t32}{t43}{t44}</FixedWidthContainer>;
    $[93] = t32;
    $[94] = t43;
    $[95] = t44;
    $[96] = t45;
  } else {
    t45 = $[96];
  }
  let t46;
  if ($[97] !== t11 || $[98] !== t16 || $[99] !== t45) {
    t46 = <>{t11}{t16}{t45}</>;
    $[97] = t11;
    $[98] = t16;
    $[99] = t45;
    $[100] = t46;
  } else {
    t46 = $[100];
  }
  return t46;
};
CategoryAlgoliaLayout.propTypes = {
  slug: PropTypes.string.isRequired,
  pageType: PropTypes.oneOf(Object.values(PAGE_TYPE)),
  serverCategoryData: PropTypes.object,
  // False when the consumer app's generateMetadata could not resolve the
  // page metadata; this layout then emits the tags from categoryData.
  serverSeoResolved: PropTypes.bool,
  initialProducts: PropTypes.array,
  selectedFacets: PropTypes.object,
  onToggleFacet: PropTypes.func,
  onClearFacets: PropTypes.func
};
export default CategoryAlgoliaLayout;
function _temp(prev) {
  return !prev;
}